import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Calendar,
  Coffee,
  Users,
  Eye,
  Briefcase,
  Settings,
  ArrowRight,
  Activity,
} from 'lucide-react'
import { apiFetch } from '../../config/api'
import PageHeader from '../../components/Dashboard/PageHeader'

type StatCard = {
  label: string
  value: string
  hint: string
  icon: typeof Calendar
  to: string
}

export default function DashboardHome() {
  const [stats, setStats] = useState<StatCard[]>([
    { label: 'Aktive Events', value: '—', hint: 'Status Aktiv', icon: Calendar, to: '/dashboard/events' },
    { label: 'Brunch', value: '—', hint: 'Einträge gesamt', icon: Coffee, to: '/dashboard/brunches' },
    { label: 'Benutzer', value: '—', hint: 'Zugänge', icon: Users, to: '/dashboard/users' },
    { label: 'Besucher heute', value: '—', hint: 'Öffentliche Seiten', icon: Eye, to: '/dashboard/statistik' },
  ])
  const [currentUser, setCurrentUser] = useState<{ name?: string } | null>(null)
  const [serverUptime, setServerUptime] = useState<string | null>(null)
  const [serverResponseMs, setServerResponseMs] = useState<number | null>(null)
  const [pageViews7d, setPageViews7d] = useState<number | null>(null)
  const [serverOnline, setServerOnline] = useState(true)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  useEffect(() => {
    const userStr = localStorage.getItem('dashboard_user')
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr))
      } catch (e) {
        console.error('Error parsing user data:', e)
      }
    }
  }, [])

  useEffect(() => {
    const token = getAuthToken()
    if (!token) return

    const countFrom = async (path: string, pick: (data: any) => number) => {
      try {
        const res = await apiFetch(path, { headers: { Authorization: token } })
        const data = await res.json()
        return data.success ? pick(data) : 0
      } catch {
        return 0
      }
    }

    const load = async () => {
      const [eventCount, brunchCount, userCount, visitorsToday] = await Promise.all([
        countFrom('/events', (d) =>
          Array.isArray(d.data) ? d.data.filter((e: any) => e.status === 'Aktiv').length : 0
        ),
        countFrom('/brunches', (d) => (Array.isArray(d.data) ? d.data.length : 0)),
        countFrom('/users', (d) => (Array.isArray(d.users) ? d.users.length : 0)),
        (async () => {
          try {
            const start = performance.now()
            const res = await apiFetch('/stats', { headers: { Authorization: token } })
            setServerResponseMs(Math.round(performance.now() - start))
            if (!res.ok) {
              setServerOnline(false)
              return 0
            }
            const data = await res.json()
            if (data.success) {
              setServerOnline(data.server?.online ?? true)
              setServerUptime(data.server?.uptime ?? null)
              setPageViews7d(data.page_views_7d ?? 0)
              return data.visitors_today ?? 0
            }
          } catch {
            setServerOnline(false)
          }
          return 0
        })(),
      ])

      setStats([
        { label: 'Aktive Events', value: String(eventCount), hint: 'Status Aktiv', icon: Calendar, to: '/dashboard/events' },
        { label: 'Brunch', value: String(brunchCount), hint: 'Einträge gesamt', icon: Coffee, to: '/dashboard/brunches' },
        { label: 'Benutzer', value: String(userCount), hint: 'Zugänge', icon: Users, to: '/dashboard/users' },
        { label: 'Besucher heute', value: String(visitorsToday), hint: 'Öffentliche Seiten', icon: Eye, to: '/dashboard/statistik' },
      ])
    }

    load()
  }, [])

  const greeting =
    new Date().getHours() < 12 ? 'Guten Morgen' : new Date().getHours() < 18 ? 'Guten Tag' : 'Guten Abend'
  const today = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  const shortcuts = [
    { to: '/dashboard/events', icon: Calendar, label: 'Event anlegen' },
    { to: '/dashboard/brunches', icon: Coffee, label: 'Brunch verwalten' },
    { to: '/dashboard/bewerbung', icon: Briefcase, label: 'Stellenanzeigen' },
    { to: '/dashboard/users', icon: Users, label: 'Benutzer' },
    { to: '/dashboard/statistik', icon: Eye, label: 'Statistiken' },
    { to: '/dashboard/settings', icon: Settings, label: 'Einstellungen' },
  ]

  return (
    <div>
      <PageHeader
        title={`${greeting}${currentUser?.name ? `, ${currentUser.name}` : ''}`}
        description={today}
      />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-6">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <Link
              key={stat.label}
              to={stat.to}
              className="group rounded-lg border border-gray-800 bg-gray-950 p-4 hover:border-gray-700 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-zinc-500">{stat.label}</span>
                <Icon className="h-4 w-4 text-zinc-500 group-hover:text-brand-400" />
              </div>
              <p className="text-2xl font-semibold text-white tabular-nums">{stat.value}</p>
              <p className="text-xs text-zinc-500 mt-1">{stat.hint}</p>
            </Link>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <section className="lg:col-span-2 rounded-lg border border-gray-800 bg-gray-950 p-4">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="h-4 w-4 text-zinc-500" />
            <h2 className="text-sm font-semibold text-white">Status</h2>
          </div>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Server</dt>
              <dd className="flex items-center gap-2 text-white">
                <span className={`h-1.5 w-1.5 rounded-full ${serverOnline ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {serverOnline ? 'Online' : 'Offline'}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Uptime</dt>
              <dd className="text-white tabular-nums">{serverUptime ?? '—'}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Antwortzeit</dt>
              <dd className="text-white tabular-nums">
                {serverResponseMs != null ? `${serverResponseMs} ms` : '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-zinc-400">Aufrufe (7 Tage)</dt>
              <dd className="text-white tabular-nums">{pageViews7d ?? '—'}</dd>
            </div>
          </dl>
        </section>

        <section className="lg:col-span-3 rounded-lg border border-gray-800 bg-gray-950 p-4">
          <h2 className="text-sm font-semibold text-white mb-4">Schnellzugriff</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {shortcuts.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 h-11 px-3 rounded-md border border-gray-800 hover:bg-white/5 hover:border-gray-700 text-sm text-zinc-200"
                >
                  <Icon className="h-4 w-4 text-zinc-500" />
                  <span className="flex-1">{item.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-zinc-600" />
                </Link>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
